import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { adminRegistrationProductsAPI } from '../services/adminApi';
import { useAdminAuth } from '../contexts/AdminAuthContext';
import { getAdminTenantId } from '../utils/tenantHelper';
import FormattedProductDescription from '../utils/FormattedProductDescription';
import ProductDescriptionEditor from './ProductDescriptionEditor';

const Container = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 2rem;
  max-width: 880px;
`;

const Title = styled.h2`
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #1f2937;
`;

const Subtitle = styled.p`
  margin: 0 0 1.5rem 0;
  color: #6b7280;
  font-size: 0.875rem;
`;

const FormSection = styled.div`
  background: #f8fafc;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  margin-bottom: 2rem;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  margin-bottom: 1rem;
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  &.fullWidth {
    grid-column: 1 / -1;
  }
`;

const Label = styled.label`
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
`;

const Input = styled.input`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
`;

const Select = styled.select`
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 0.875rem;
  background: #fff;
`;

const PRODUCT_KIND_OPTIONS = [
  {
    value: 'entry_pass',
    label: 'League Pass (required entry)',
    hint: 'Shown on the registration package step. Only one active League Pass per tenant.',
  },
  {
    value: 'pass_upgrade',
    label: 'Package upgrade (Team / Pro)',
    hint: 'Shown on the registration package step. Player picks League Pass or one upgrade.',
  },
  {
    value: 'addon',
    label: 'Add-on (legacy)',
    hint: 'Not shown on the player registration package step (packs only).',
  },
];

function productKindLabel(kind) {
  if (kind === 'entry_pass') return 'League Pass';
  if (kind === 'pass_upgrade') return 'Package upgrade';
  return 'Add-on';
}

const FieldHint = styled.p`
  margin: 0.35rem 0 0;
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.4;
`;

const Button = styled.button`
  padding: 0.65rem 1.1rem;
  border-radius: 6px;
  border: none;
  font-weight: 600;
  cursor: pointer;
  background: ${(p) => (p.$secondary ? '#e5e7eb' : '#043663')};
  color: ${(p) => (p.$secondary ? '#111827' : '#fff')};
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
  th,
  td {
    border-bottom: 1px solid #e5e7eb;
    padding: 0.75rem;
    text-align: left;
    vertical-align: top;
  }
  th {
    color: #6b7280;
    font-weight: 600;
  }
`;

const Thumb = styled.img`
  width: 48px;
  height: 48px;
  object-fit: cover;
  border-radius: 6px;
  background: #f3f4f6;
`;

const emptyForm = () => ({
  name: '',
  description: '',
  priceChf: '0',
  sortOrder: '0',
  isActive: true,
  isDefault: false,
  includesMerchandise: false,
  freeShipping: false,
  productKind: 'pass_upgrade',
});

const AdminSettingsRegistrationProducts = () => {
  const { adminUser } = useAdminAuth();
  const canEdit =
    adminUser?.isSuperAdmin ||
    adminUser?.permissions?.settings_registration_products?.edit ||
    adminUser?.permissions?.settings_registration_products?.create;
  const queryClient = useQueryClient();
  const adminTenantId = getAdminTenantId();
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [message, setMessage] = useState(null);
  const [deliveryFee, setDeliveryFee] = useState('7.5');
  const [contactEmail, setContactEmail] = useState('');

  const { data, isLoading } = useQuery(
    ['admin-registration-products', adminTenantId],
    () => adminRegistrationProductsAPI.getAll(),
    { staleTime: 30_000, enabled: !!adminTenantId }
  );
  const products = data?.data || data || [];

  const { data: deliveryData } = useQuery(
    ['admin-registration-delivery-settings'],
    () => adminRegistrationProductsAPI.getDeliverySettings(),
    { staleTime: 30_000 }
  );

  useEffect(() => {
    const d = deliveryData?.data || deliveryData;
    if (d) {
      setDeliveryFee(String(d.deliveryFeeChf ?? 7.5));
      setContactEmail(d.contactEmail || '');
    }
  }, [deliveryData]);

  const saveDeliveryMutation = useMutation(
    (fee) => adminRegistrationProductsAPI.updateDeliverySettings({ deliveryFeeChf: fee }),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries(['admin-registration-delivery-settings']);
        const d = res?.data || res;
        setMessage({
          type: 'ok',
          text: `Shipping fee saved: CHF ${Number(d.deliveryFeeChf).toFixed(2)}`,
        });
      },
      onError: (err) => {
        setMessage({ type: 'err', text: err.response?.data?.message || 'Failed to save shipping fee' });
      },
    }
  );

  const saveMutation = useMutation(
    async (payload) => {
      if (editingId) {
        return adminRegistrationProductsAPI.update(editingId, payload);
      }
      return adminRegistrationProductsAPI.create(payload);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['admin-registration-products']);
        setForm(emptyForm());
        setEditingId(null);
        setMessage({ type: 'ok', text: editingId ? 'Product updated' : 'Product created' });
      },
      onError: (err) => {
        setMessage({ type: 'err', text: err.response?.data?.message || 'Save failed' });
      },
    }
  );

  const deleteMutation = useMutation((id) => adminRegistrationProductsAPI.delete(id), {
    onSuccess: () => queryClient.invalidateQueries(['admin-registration-products']),
  });

  const onSubmit = (e) => {
    e.preventDefault();
    const price = Number(form.priceChf);
    if (!Number.isFinite(price) || price < 0) {
      setMessage({ type: 'err', text: 'Enter a valid price (CHF ≥ 0).' });
      return;
    }
    const merch = !!form.includesMerchandise;
    saveMutation.mutate({
      name: form.name,
      description: form.description || null,
      isActive: !!form.isActive,
      isDefault: !!form.isDefault && !!form.isActive,
      sortOrder: parseInt(form.sortOrder, 10) || 0,
      productKind: form.productKind || 'pass_upgrade',
      includesMerchandise: merch,
      freeShipping: merch && !!form.freeShipping,
      upgradeSort: 0,
      includedAddonProductIds: [],
      basePriceChf: price,
      variants: [
        {
          optionLabel: 'Default',
          sizeLabel: null,
          colorLabel: null,
          unitPriceChf: price,
          sortOrder: 0,
          isActive: true,
        },
      ],
    });
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    const price =
      p.variants?.[0] != null
        ? Number(p.variants[0].unitPriceChf ?? p.variants[0].priceDeltaChf ?? p.basePriceChf ?? 0)
        : Number(p.basePriceChf ?? 0);
    setForm({
      name: p.name,
      description: p.description || '',
      priceChf: String(price),
      sortOrder: String(p.sortOrder ?? 0),
      isActive: !!p.isActive,
      isDefault: !!p.isDefault,
      includesMerchandise: !!p.includesMerchandise,
      freeShipping: !!p.freeShipping,
      productKind: p.productKind || 'pass_upgrade',
    });
  };

  const onUpload = async (productId, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('image', file);
    try {
      await adminRegistrationProductsAPI.uploadImage(productId, fd);
      queryClient.invalidateQueries(['admin-registration-products']);
      setMessage({ type: 'ok', text: 'Image uploaded' });
    } catch (err) {
      setMessage({ type: 'err', text: err.response?.data?.message || 'Upload failed' });
    }
  };

  const productPrice = (p) => {
    if (p.variants?.[0]) {
      return Number(p.variants[0].unitPriceChf ?? p.variants[0].priceDeltaChf ?? 0);
    }
    return Number(p.basePriceChf ?? 0);
  };

  return (
    <Container>
      <Title>Registration products</Title>
      <Subtitle>
        Player registration shows active <strong>League Pass</strong> and <strong>package upgrades</strong>{' '}
        only. Set the type for each product below, then add title, description, and price. Mark one as{' '}
        <strong>default</strong> (pre-selected + “Most popular”). Turn on merchandise to collect a delivery
        address; optionally offer free shipping.
      </Subtitle>

      {message && (
        <p style={{ color: message.type === 'err' ? '#b91c1c' : '#047857' }}>{message.text}</p>
      )}

      <FormSection>
        <Label>Merchandise shipping fee (CHF)</Label>
        <p style={{ margin: '0 0 0.75rem', fontSize: '0.8rem', color: '#6b7280' }}>
          Charged for home delivery when a selected product includes merchandise and is not marked free
          shipping. Pickup is free. Order notices go to the tenant contact email
          {contactEmail ? (
            <>
              : <strong>{contactEmail}</strong>
            </>
          ) : (
            ' (set under Settings → General)'
          )}
          .
        </p>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <Input
            type="number"
            step="0.01"
            min="0"
            style={{ maxWidth: 140 }}
            value={deliveryFee}
            onChange={(e) => setDeliveryFee(e.target.value)}
            disabled={!canEdit}
          />
          {canEdit && (
            <Button
              type="button"
              disabled={saveDeliveryMutation.isLoading}
              onClick={() => saveDeliveryMutation.mutate(Number(deliveryFee))}
            >
              Save shipping fee
            </Button>
          )}
        </div>
      </FormSection>

      {canEdit && (
        <FormSection>
          <form onSubmit={onSubmit}>
            <FormGrid>
              <FormGroup>
                <Label>Title</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  required
                  placeholder="e.g. Team package"
                />
              </FormGroup>
              <FormGroup>
                <Label>Price (CHF)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.priceChf}
                  onChange={(e) => setForm({ ...form, priceChf: e.target.value })}
                  required
                />
              </FormGroup>
              <FormGroup>
                <Label>Sort order</Label>
                <Input
                  type="number"
                  value={form.sortOrder}
                  onChange={(e) => setForm({ ...form, sortOrder: e.target.value })}
                />
              </FormGroup>
              <FormGroup className="fullWidth">
                <Label htmlFor="registration-product-kind">Registration type</Label>
                <Select
                  id="registration-product-kind"
                  value={form.productKind}
                  onChange={(e) => {
                    const productKind = e.target.value;
                    setForm({
                      ...form,
                      productKind,
                      includesMerchandise:
                        productKind === 'pass_upgrade' ? true : form.includesMerchandise,
                    });
                  }}
                >
                  {PRODUCT_KIND_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
                <FieldHint>
                  {PRODUCT_KIND_OPTIONS.find((o) => o.value === form.productKind)?.hint ||
                    'Choose League Pass or a package upgrade so the product appears during registration.'}
                </FieldHint>
              </FormGroup>
              <FormGroup>
                <Label>
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        isActive: e.target.checked,
                        isDefault: e.target.checked ? form.isDefault : false,
                      })
                    }
                  />{' '}
                  Active
                </Label>
              </FormGroup>
              <FormGroup className="fullWidth">
                <Label>Description</Label>
                <ProductDescriptionEditor
                  value={form.description}
                  onChange={(html) => setForm({ ...form, description: html })}
                />
                <FieldHint>
                  List bullets are always brand red; text colour can be set separately with Text colour.
                  Prefer 12–16pt for package cards — larger sizes are auto-capped in the catalog.
                </FieldHint>
              </FormGroup>
              <FormGroup className="fullWidth">
                <Label>
                  <input
                    type="checkbox"
                    checked={!!form.isDefault}
                    disabled={!form.isActive}
                    onChange={(e) => setForm({ ...form, isDefault: e.target.checked })}
                  />{' '}
                  Default package (pre-selected + “Most popular”)
                </Label>
                <FieldHint>Only one active product can be the default.</FieldHint>
              </FormGroup>
              <FormGroup className="fullWidth">
                <Label>
                  <input
                    type="checkbox"
                    checked={form.includesMerchandise}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        includesMerchandise: e.target.checked,
                        freeShipping: e.target.checked ? form.freeShipping : false,
                      })
                    }
                  />{' '}
                  Includes merchandise (adds delivery address step)
                </Label>
              </FormGroup>
              {form.includesMerchandise ? (
                <FormGroup className="fullWidth">
                  <Label>
                    <input
                      type="checkbox"
                      checked={form.freeShipping}
                      onChange={(e) => setForm({ ...form, freeShipping: e.target.checked })}
                    />{' '}
                    Free shipping (address still collected; no shipping fee)
                  </Label>
                </FormGroup>
              ) : null}
            </FormGrid>

            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.75rem' }}>
              <Button type="submit" disabled={saveMutation.isLoading}>
                {editingId ? 'Update product' : 'Create product'}
              </Button>
              {editingId && (
                <Button
                  type="button"
                  $secondary
                  onClick={() => {
                    setEditingId(null);
                    setForm(emptyForm());
                  }}
                >
                  Cancel
                </Button>
              )}
            </div>
          </form>
        </FormSection>
      )}

      {isLoading ? (
        <p>Loading…</p>
      ) : (
        <Table>
          <thead>
            <tr>
              <th>Image</th>
              <th>Product</th>
              <th>Type</th>
              <th>Price</th>
              <th>Options</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {(Array.isArray(products) ? products : []).map((p) => (
              <tr key={p.id}>
                <td>
                  {p.hasImage ? (
                    <Thumb src={p.imageUrl} alt="" />
                  ) : (
                    <span style={{ color: '#9ca3af' }}>—</span>
                  )}
                  {canEdit && (
                    <div>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => onUpload(p.id, e.target.files?.[0])}
                      />
                    </div>
                  )}
                </td>
                <td>
                  <strong>{p.name}</strong>
                  {p.isDefault ? (
                    <span
                      style={{
                        marginLeft: 8,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        color: '#D52B1E',
                      }}
                    >
                      Most popular
                    </span>
                  ) : null}
                  {p.description ? (
                    <div style={{ color: '#6b7280', marginTop: 4 }}>
                      <FormattedProductDescription text={p.description} density="card" clamp lines={3} />
                    </div>
                  ) : null}
                </td>
                <td>{productKindLabel(p.productKind)}</td>
                <td>CHF {productPrice(p).toFixed(2)}</td>
                <td>
                  {p.includesMerchandise ? (p.freeShipping ? 'Merch · free ship' : 'Merch') : '—'}
                  {p.isDefault ? ' · default' : ''}
                </td>
                <td>{p.isActive ? 'Active' : 'Inactive'}</td>
                <td>
                  {canEdit && (
                    <>
                      <Button type="button" $secondary onClick={() => startEdit(p)}>
                        Edit
                      </Button>{' '}
                      <Button
                        type="button"
                        $secondary
                        onClick={() => {
                          if (window.confirm('Delete this product?')) deleteMutation.mutate(p.id);
                        }}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Container>
  );
};

export default AdminSettingsRegistrationProducts;
